import { ApiType } from "@/types";
import * as Sentry from "@sentry/react";
import axios, { AxiosResponse } from "axios";

const OPS_API_URL = import.meta.env.VITE_OPS_API_URL;

// 日程調整の有効期限切れ。API は個人情報を返さずに 410 Gone を返す
const EXPIRED_STATUS = 410;

// クエリ（AxiosResponse を throw）とミューテーション（AxiosError を throw）の両方を判定する
export const isExpiredError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) return error.response?.status === EXPIRED_STATUS;
  return (error as AxiosResponse | undefined)?.status === EXPIRED_STATUS;
};

const captureException = (e: unknown) => {
  // 期限切れは想定内のため Sentry に送らない
  if (isExpiredError(e)) return;
  Sentry.captureException(e);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const checkRedirection = (response: AxiosResponse<any, any>) => {
  const url = response.config.url;
  const responseUrl = response.request.responseURL;
  if (url != responseUrl) {
    const error = new Error(
      `url ${url} is redirected to ${responseUrl}. Please check the API.`
    );
    console.log({ error });
    Sentry.captureException(error);
  }
};

export class Api {
  static async get({
    path,
    type,
    params = {},
  }: {
    path: string;
    type?: ApiType;
    params?: Record<string, unknown>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }): Promise<any> {
    try {
      const response = await axios.get(`${apiDomain(type)}/${path}`, {
        params,
      });
      checkRedirection(response);
      return response;
    } catch (e) {
      captureException(e);
      throw e;
    }
  }

  static async post({
    path,
    type,
    data,
  }: {
    path: string;
    type?: ApiType;
    data: Record<string, unknown>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }): Promise<any> {
    try {
      return await axios.post(`${apiDomain(type)}/${path}`, data);
    } catch (e) {
      captureException(e);
      throw e;
    }
  }

  static async patch({
    path,
    type,
    data,
  }: {
    path: string;
    type?: ApiType;
    data: Record<string, unknown>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }): Promise<any> {
    try {
      return await axios.patch(`${apiDomain(type)}/${path}`, data);
    } catch (e) {
      captureException(e);
      throw e;
    }
  }

  static async putForS3({
    url,
    data,
    headers = {},
  }: {
    url: string;
    data: File | Blob | ArrayBuffer | string;
    headers?: Record<string, string>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }): Promise<any> {
    try {
      return await axios.put(url, data, {
        headers,
        // デフォルトのtransformRequestを無効化（S3アップロード用）
        transformRequest: [(data) => data],
        // タイムアウト設定
        timeout: 60000,
      });
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  }

}

const apiDomain = (apiType?: ApiType): string => {
  if (!apiType) return OPS_API_URL;

  switch (apiType) {
    case "ops":
      return OPS_API_URL;
    default:
      return "";
  }
};
